import { Dialog, DialogContext } from '@ark-ui/react/dialog'
import { Portal } from '@ark-ui/react/portal'
import { captureException } from '@sentry/react'
import { Input } from 'grafbase-components/inputs/input'
import { InputRoot } from 'grafbase-components/inputs/input-root'
import { Select } from 'grafbase-components/inputs/select'
import { Textarea } from 'grafbase-components/inputs/textarea'
import { toast } from 'grafbase-components/lib/toast'
import { Button } from 'grafbase-components/ui/button/button'
import { Icon } from 'grafbase-components/ui/icon/icon'
import type { PropsWithChildren } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { graphql, useFragment } from 'react-relay'
import type { helpModalFragment$key } from '#gen/relay/helpModalFragment.graphql'
import { ThreadPriority } from '#lib/plain/types'
import { modalStyles } from '#lib/ui/modal/modal.css'
import { PriorityIcon } from '#lib/ui/priority-icon/priority-icon'
import { createSupportTicket } from '../account/support/create-support-ticket'
import { helpModalStyles as styles } from './help-modal.css'

const query = graphql`
  fragment helpModalFragment on User {
    organizations {
      nodes {
        name
        slug
      }
    }
  }
`

interface Props {
  activeAccountSlug?: string
  dataRef: helpModalFragment$key
}

export function HelpModal(props: PropsWithChildren<Props>) {
  const user = useFragment(query, props.dataRef)

  if (user.organizations.nodes.length === 0)
    return <DiscordModal>{props.children}</DiscordModal>

  return <PlainModal {...props}>{props.children}</PlainModal>
}

function PlainModal(props: PropsWithChildren<Props>) {
  const user = useFragment(query, props.dataRef)

  const defaultAccountSlug =
    user.organizations.nodes.find(org => org.slug === props.activeAccountSlug)
      ?.slug ?? user.organizations.nodes[0]!.slug

  const form = useForm({
    defaultValues: {
      accountSlug: defaultAccountSlug,
      subject: '',
      description: '',
      priority: ThreadPriority.Normal,
    },
  })

  return (
    <Dialog.Root onOpenChange={() => form.reset()}>
      <DialogContext>
        {dialog => {
          const onSubmit = form.handleSubmit(async data => {
            try {
              await createSupportTicket({
                accountSlug: data.accountSlug,
                subject: data.subject,
                description: data.description,
                priority: data.priority,
              })

              dialog.setOpen(false)
              toast.success({
                title: 'We have received your support request',
                description: 'We will reply to your message shortly.',
              })
            } catch (error) {
              const message = 'Failed to submit support request'
              toast.error({ title: message })
              captureException(new Error(message, { cause: error }))
            }
          })

          return (
            <>
              {props.children}

              <Portal>
                <Dialog.Backdrop className={modalStyles.backdrop} />

                <Dialog.Positioner className={modalStyles.positioner}>
                  <Dialog.Content className={modalStyles.content}>
                    <Dialog.Title className={modalStyles.title()}>
                      Help
                    </Dialog.Title>
                    <form onSubmit={onSubmit} className={styles.form}>
                      <InputRoot label="Account" reserveSpaceForError>
                        <Controller
                          control={form.control}
                          name="accountSlug"
                          rules={{ required: true }}
                          render={({ field: { value, ...field } }) => (
                            <Select
                              isInsideModal
                              {...field}
                              required
                              items={user.organizations.nodes.map(org => ({
                                label: org.name,
                                value: org.slug,
                              }))}
                              defaultValue={[value]}
                              value={[value]}
                              onValueChange={value => {
                                if (value.value[0])
                                  form.setValue('accountSlug', value.value[0])
                              }}
                            />
                          )}
                        />
                      </InputRoot>

                      <InputRoot label="Subject" reserveSpaceForError>
                        <Input
                          {...form.register('subject', {
                            required: {
                              value: true,
                              message: 'Please enter a subject',
                            },
                          })}
                          error={form.formState.errors.subject?.message}
                        />
                      </InputRoot>

                      <InputRoot label="Description" reserveSpaceForError>
                        <Textarea
                          {...form.register('description', {
                            required: {
                              value: true,
                              message: 'Please enter a description',
                            },
                          })}
                          placeholder="Please describe your issue or question in detail"
                          rows={5}
                          error={form.formState.errors.description?.message}
                        />
                      </InputRoot>

                      <InputRoot label="Priority" reserveSpaceForError>
                        <Controller
                          control={form.control}
                          name="priority"
                          rules={{ required: true }}
                          render={({ field: { value, ...field } }) => (
                            <Select
                              isInsideModal
                              {...field}
                              required
                              items={[
                                {
                                  label: 'Urgent',
                                  value: ThreadPriority.Urgent,
                                },
                                {
                                  label: 'High',
                                  value: ThreadPriority.High,
                                },
                                {
                                  label: 'Normal',
                                  value: ThreadPriority.Normal,
                                },
                                {
                                  label: 'Low',
                                  value: ThreadPriority.Low,
                                },
                              ]}
                              itemToElement={item => (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    flexGrow: 1,
                                    gap: 8,
                                  }}
                                >
                                  <PriorityIcon value={item.value} size="sm" />
                                  {item.label}
                                </div>
                              )}
                              value={[value.toString()]}
                              onValueChange={value => {
                                if (value.value[0])
                                  form.setValue(
                                    'priority',
                                    Number(value.value[0]),
                                  )
                              }}
                            />
                          )}
                        />
                      </InputRoot>

                      <div className={modalStyles.buttonContainer}>
                        <Button
                          type="submit"
                          variant="white"
                          isLoading={form.formState.isSubmitting}
                        >
                          Submit
                        </Button>
                        <Dialog.CloseTrigger asChild>
                          <Button type="button" variant="secondary">
                            Cancel
                          </Button>
                        </Dialog.CloseTrigger>
                      </div>
                    </form>
                  </Dialog.Content>
                </Dialog.Positioner>
              </Portal>
            </>
          )
        }}
      </DialogContext>
    </Dialog.Root>
  )
}

function DiscordModal(props: PropsWithChildren) {
  return (
    <Dialog.Root unmountOnExit lazyMount>
      {props.children}

      <Portal>
        <Dialog.Backdrop className={modalStyles.backdrop} />

        <Dialog.Positioner className={modalStyles.positioner}>
          <Dialog.Content className={styles.discordModalContent}>
            <h3 className={styles.discordModalTitle}>Support Request</h3>

            <p className={styles.discordModalDescription}>
              Join us on Discord. Talk about what you{"'"}re working on, get
              answers to common questions, and help shape the future of
              Grafbase.
            </p>

            <a
              aria-label="Join the Grafbase Discord"
              href="https://discord.gg/grafbase"
              target="_blank"
            >
              <Icon icon="discord" size="2xl" />
            </a>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}
